import React, { useCallback, useState } from 'react'
import T from 'prop-types'
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  CAR_CHOICE_ACCEPTED,
  CAR_CHOICE_ACCEPTED_WITH_REPRICE,
  CARS_BOOKED_NOT_INTERESTED,
} from 'lib/carChoicesLib'

import ButtonLink from 'components/atoms/ButtonLink'

import useConfirmationDialog from 'hooks/useConfirmationDialog'

import ChoiceLabel from './ChoiceLabel'
import RepriceDialog from './RepriceDialog'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'

export default function CarCardChoices({
  variant = VARIANT_LIST,
  session,
  partnerId,
  car,
  updateChoice,
  readOnly = false,
}) {
  const { t } = useTranslation()

  const [repriceLocalValue, setRepriceLocalValue] = useState('')

  const isDetails = variant === VARIANT_DETAILS

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(updateChoice)

  const handleUpdateChoice = useCallback(
    (vin, choice) => {
      if (choice === CAR_CHOICE_ACCEPTED_WITH_REPRICE) {
        handleOpenConfirmationDialog()
      } else {
        updateChoice(session.sessionId, partnerId, vin, choice)
      }
    },
    [handleOpenConfirmationDialog, partnerId, session.sessionId, updateChoice]
  )

  const handleLocalChange = async (e) => {
    const choice = e.target.id.split('.')[1]

    await handleUpdateChoice(car.vin, choice)
  }

  const handleConfirmRepriceChoice = useCallback(
    (vin, reprice) => {
      handleConfirmConfirmationDialog(
        session.sessionId,
        partnerId,
        vin,
        CAR_CHOICE_ACCEPTED_WITH_REPRICE,
        reprice
      )
    },
    [partnerId, session.sessionId, handleConfirmConfirmationDialog]
  )

  return (
    <>
      <Stack
        alignItems="flex-start"
        sx={{ width: 1, minWidth: '15ch' }}
        data-testid="car-card-choices"
      >
        <RadioGroup
          name={`${car.vin}.radioGroup`}
          defaultValue="none"
          onChange={handleLocalChange}
        >
          <FormControlLabel
            disabled={readOnly}
            control={
              <Radio
                checked={car.choice === CAR_CHOICE_ACCEPTED}
                id={`${car.vin}.${CAR_CHOICE_ACCEPTED}`}
                data-testid={`${car.vin}.${CAR_CHOICE_ACCEPTED}`}
                value={CAR_CHOICE_ACCEPTED}
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t(`carsToBuy.choice.${CAR_CHOICE_ACCEPTED}`)}
              />
            }
          />
          <Box pb={isDetails ? 0 : 1} />
          <FormControlLabel
            disabled={readOnly}
            control={
              <Radio
                checked={car.choice === CAR_CHOICE_ACCEPTED_WITH_REPRICE}
                id={`${car.vin}.${CAR_CHOICE_ACCEPTED_WITH_REPRICE}`}
                data-testid={`${car.vin}.${CAR_CHOICE_ACCEPTED_WITH_REPRICE}`}
                value={CAR_CHOICE_ACCEPTED_WITH_REPRICE}
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t(
                  `carsToBuy.choice.${CAR_CHOICE_ACCEPTED_WITH_REPRICE}`
                )}
              />
            }
          />
          {car.choice === CAR_CHOICE_ACCEPTED_WITH_REPRICE && (
            <>
              <Box display="flex" alignItems="center" pl={'32px'}>
                <Typography variant="caption" color="primary">
                  <strong>
                    € {car.reprice ? numberFormatter.format(car.reprice) : '-'}
                  </strong>
                </Typography>
                <Box pr={2} />
                <ButtonLink
                  color="textPrimary"
                  variant="caption"
                  onClick={() =>
                    handleUpdateChoice(
                      car.vin,
                      CAR_CHOICE_ACCEPTED_WITH_REPRICE
                    )
                  }
                  disabled={readOnly}
                >
                  <strong>{t('carsToBuy.reprice.changeReprice')}</strong>
                </ButtonLink>
              </Box>
            </>
          )}
          <Box pb={isDetails ? 0 : 1} />

          <FormControlLabel
            disabled={readOnly}
            control={
              <Radio
                checked={car.choice === null}
                id={`${car.vin}.${CARS_BOOKED_NOT_INTERESTED}`}
                data-testid={`${car.vin}.${CARS_BOOKED_NOT_INTERESTED}`}
                value={CARS_BOOKED_NOT_INTERESTED}
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t(`carsToBuy.choice.${CARS_BOOKED_NOT_INTERESTED}`)}
              />
            }
          />
        </RadioGroup>
      </Stack>
      {openConfirmationDialog && (
        <RepriceDialog
          car={car || {}}
          repriceLocalValue={repriceLocalValue}
          setRepriceLocalValue={setRepriceLocalValue}
          openConfirmationDialog={openConfirmationDialog}
          handleCloseConfirmationDialog={handleCloseConfirmationDialog}
          handleConfirmConfirmationDialog={handleConfirmRepriceChoice}
          readOnly={readOnly}
        />
      )}
    </>
  )
}

CarCardChoices.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  session: T.object.isRequired,
  partnerId: T.string.isRequired,
  car: T.object.isRequired,
  updateChoice: T.func,
  readOnly: T.bool,
}
